import { useState, useRef, useEffect, useCallback } from 'react';
import { captureException } from '@sentry/browser';
import { debounce } from 'underscore';

import routes from 'sitewide-js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { trackClick } from 'js/utils/trackClick';
import { fsEvent, initializeFullstory } from 'js/fullstory';
import {
  createPodcastEpisode,
  generatePodcastEpisode,
  recordPodcastListenSession,
  continueGeneratePodcastEpisode,
  requestPodcast,
} from 'js/podcast_ai/api';
import { currentUser } from 'js/utils/user';
import { WorkId } from 'js/liswp/types';

const calculateSegments = (audioListenSeconds: number[]) => {
  const segments: { start: number; end: number; duration: number }[] = [];
  for (let i = 0; i < audioListenSeconds.length; i++) {
    const cur = audioListenSeconds[i];
    if (segments.length === 0) {
      segments.push({ start: cur, end: cur, duration: 0 });
    } else {
      const prev = segments[segments.length - 1];
      if (prev.end === cur) {
        continue;
      } else if (prev.end + 1 === cur) {
        prev.end = cur;
        prev.duration = cur - prev.start;
      } else {
        segments.push({ start: cur, end: cur, duration: 0 });
      }
    }
  }
  return segments;
};

// We update the listen session every 3 seconds.
const RECORD_INTERVAL = 3;

type AiPodcastEpisode = {
  id: number;
  generationStartedAt?: string | null;
  generationCompletedAt?: string | null;
  streamReadyAt?: string | null;
  transcriptCompletedAt?: string | null;
  visibility?: string;
  duration?: number | null;
};

type UsePaperToPodcastProps = {
  workId: WorkId;
  impressionSource?: string;
  initialEpisode?: AiPodcastEpisode | null;
};

export const usePaperToPodcast = ({
  workId,
  impressionSource = 'paper_to_podcast_swp',
  initialEpisode = null,
}: UsePaperToPodcastProps) => {
  const [showPlayer, setShowPlayer] = useState(false);
  const [feedbackInput, setFeedbackInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [error, setError] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [initializedStream, setInitializedStream] = useState(false);
  const [times, setTimes] = useState<Date[]>([]);
  const [episode, setEpisode] = useState<AiPodcastEpisode | null>(
    initialEpisode,
  );
  const [listenSessionId, _setListenSessionId] = useState<number>(
    Math.floor(Date.now() / 1000),
  );
  const [_audioListenSeconds, setAudioListenSeconds] = useState<number[]>([]);
  // iOS Safari fix: Store handlePause in a ref so event listeners can access latest version
  const handlePauseRef = useRef<() => void>();

  useEffect(() => {
    // Only fetch episode if not provided via initialEpisode, or if generation is not yet completed
    if (!initialEpisode || !initialEpisode.generationCompletedAt) {
      createPodcastEpisode(Number(workId)).then((res) => {
        setEpisode(res.podcast_episode);
      });
    }
  }, [workId, initialEpisode]);

  const handlePause = useCallback(async () => {
    let calculatedTime = 0;
    let timeIndex = 0;
    const newTimes = [...times, new Date()];

    // Here, `times` is an array of timestamps that alternates between
    // the time the player started and when it was paused.
    // Calculate the sum of differences between each pair to find the total
    // play time. This is mainly to supplement situations when the browser does not report the
    // player's currentTime correctly.
    if (times.length > 0) {
      while (
        typeof newTimes[timeIndex] !== 'undefined' &&
        typeof newTimes[timeIndex + 1] !== 'undefined'
      ) {
        const diff =
          newTimes[timeIndex + 1].getTime() - newTimes[timeIndex].getTime();
        calculatedTime += diff;
        timeIndex += 2;
      }
      setTimes(newTimes);
    }

    trackClick({
      source: `${impressionSource}.pause`,
      extra: {
        work_id: workId,
        current_time: audioRef.current?.currentTime || null,
        total_time: audioRef.current?.duration || null,
        total_play_time: calculatedTime,
      },
    });
  }, [times, workId, impressionSource]);

  // iOS Safari fix: Keep ref updated with latest handlePause
  useEffect(() => {
    handlePauseRef.current = handlePause;
  }, [handlePause]);

  const incrementGenerateCount = useCallback(async () => {
    fetchWithCsrf(routes.api_v0_feature_limits_path(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ feature_name: 'paper_to_podcast' }),
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const recordPodcastListenSessionDebounced = useCallback(
    debounce(recordPodcastListenSession, RECORD_INTERVAL * 1000),
    [],
  );

  const addTimeUpdateListener = useCallback(
    (audioPlayer: HTMLAudioElement) => {
      audioPlayer.addEventListener('timeupdate', () => {
        setAudioListenSeconds((prev) => {
          const newTime = Math.round(audioPlayer.currentTime);
          if (newTime === prev[prev.length - 1]) {
            return prev;
          }
          const result = [...prev, newTime];
          if (episode) {
            const segments = calculateSegments(result);
            const args: Parameters<typeof recordPodcastListenSession> = [
              Number(workId),
              Number(episode.id),
              listenSessionId,
              segments,
              segments.reduce((total, segment) => total + segment.duration, 0),
            ];
            if (newTime % RECORD_INTERVAL === 0) {
              recordPodcastListenSession(...args);
              // Continue generating more content when user is actively listening
              if (newTime >= 30 && newTime <= 60) {
                continueGeneratePodcastEpisode(Number(episode.id)).then(
                  (res) => {
                    if (!res.success)
                      setError(res.error || 'Failed to continue generation');
                  },
                );
              }
            } else {
              recordPodcastListenSessionDebounced(...args);
            }
          }
          return result;
        });
      });

      // iOS Safari fix: Add direct DOM event listeners for pause
      // React's synthetic events don't always fire with native HLS controls on iOS
      audioPlayer.addEventListener('pause', () => {
        handlePauseRef.current?.();
      });
    },
    [episode, workId, listenSessionId, recordPodcastListenSessionDebounced],
  );

  // Our server optimistically returns the playlist url, but we don't expect it to be available immediately,
  // so we retry loading the playlist for 20 seconds before giving up.
  const loadHLSPlaylistWithRetry = useCallback(
    async (playlistUrl: string) => {
      let retries = 0;
      const maxRetries = 20;
      const retryDelay = 2000;
      const audioPlayer = audioRef.current;

      if (!audioPlayer) {
        return;
      }
      addTimeUpdateListener(audioPlayer);

      const loadPlaylist = async () => {
        // Dynamically import HLS.js only when needed
        const { default: Hls } = await import('hls.js');

        return new Promise<void>((resolve, reject) => {
          const hls = new Hls({
            maxBufferLength: 2,
            startPosition: 0,
            liveDurationInfinity: true,

            manifestLoadingTimeOut: 10000, // Longer timeout for manifest loading
            manifestLoadingMaxRetry: 6, // More retries
            manifestLoadingRetryDelay: 1000, // Initial retry delay
            manifestLoadingMaxRetryTimeout: 64000, // Max retry timeout

            // Fragment loading configuration
            fragLoadingTimeOut: 20000,
            fragLoadingMaxRetry: 6,
            fragLoadingRetryDelay: 1000,
            fragLoadingMaxRetryTimeout: 64000,
          });
          hls.loadSource(playlistUrl);
          hls.attachMedia(audioPlayer);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            setLoading(false);
            setShowPlayer(true);
            audioPlayer.play().catch((error) => {
              console.error('Autoplay failed:', error);
            });
            resolve();
          });
          hls.on(Hls.Events.ERROR, (_, data) => {
            fsEvent(`${impressionSource}_failed`, {
              reason: data.reason,
            });
            hls.destroy(); // Clean up the HLS instance
            reject(data);
          });
        });
      };

      while (retries < maxRetries) {
        try {
          await loadPlaylist();
          return;
        } catch {
          retries++;
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
      setError('Failed to load playlist after maximum retries.');
    },
    [addTimeUpdateListener, impressionSource],
  );

  // Our server optimistically returns the playlist url, but we don't expect it to be available immediately,
  // so we retry loading the playlist for 20 seconds before giving up.
  // TODO: more testing needed for this function
  const loadNativePlaylistWithRetry = useCallback(
    async (playlistUrl: string) => {
      let retries = 0;
      const maxRetries = 20;
      const retryDelay = 2000;
      const audioPlayer = audioRef.current;
      while (retries < maxRetries && audioPlayer) {
        try {
          audioPlayer.src = playlistUrl;
          audioPlayer.load();

          await new Promise((resolve, reject) => {
            const onError = (error: ErrorEvent) => {
              fsEvent(`${impressionSource}_failed`, {
                error_type: error.type,
                error_message: error.message,
                error_timeStamp: error.timeStamp,
              });
              audioPlayer.removeEventListener('canplaythrough', onLoadSuccess);
              audioPlayer.removeEventListener('error', (e) => onError(e));
              audioPlayer.removeEventListener('loadedmetadata', onLoadSuccess);
              reject(new Error('Failed to load media'));
            };

            const onLoadSuccess = () => {
              audioPlayer.removeEventListener('canplaythrough', onLoadSuccess);
              audioPlayer.removeEventListener('error', (e) => onError(e));
              audioPlayer.removeEventListener('loadedmetadata', onLoadSuccess);
              resolve(true);
            };

            // iOS Safari fix: Listen to both canplaythrough and loadedmetadata
            // loadedmetadata fires earlier and ensures controls are properly initialized
            addTimeUpdateListener(audioPlayer);
            audioPlayer.addEventListener('loadedmetadata', onLoadSuccess, {
              once: true,
            });
            audioPlayer.addEventListener('canplaythrough', onLoadSuccess, {
              once: true,
            });
            audioPlayer.addEventListener('error', (e) => onError(e), {
              once: true,
            });
          });

          // iOS Safari fix: Small delay to ensure controls are ready
          await new Promise((resolve) => setTimeout(resolve, 100));

          audioPlayer.play().catch((error) => {
            console.error('Autoplay failed:', error);
          });
          setLoading(false);
          setShowPlayer(true);
          return;
        } catch {
          retries++;
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }
      setError('Failed to load playlist after maximum retries.');
    },
    [addTimeUpdateListener, impressionSource],
  );

  const handleAudioStream = useCallback(async () => {
    initializeFullstory();
    fsEvent(`${impressionSource}_clicked`);
    setLoading(true);
    let res;

    try {
      res = await generatePodcastEpisode(Number(episode?.id));
    } catch {
      setError('Failed to generate audio stream');
      return;
    }
    const { url } = res;

    // set the audio src to the hls stream
    const audioPlayer = audioRef.current;
    if (
      audioPlayer &&
      (audioPlayer.canPlayType('application/vnd.apple.mpegurl') ||
        audioPlayer.canPlayType('audio/x-mpegurl'))
    ) {
      loadNativePlaylistWithRetry(url);
    } else {
      // Check if HLS is supported before attempting to load
      try {
        const { default: Hls } = await import('hls.js');
        if (Hls.isSupported()) {
          loadHLSPlaylistWithRetry(url);
        } else {
          throw new Error('HLS is not supported in this browser.');
        }
      } catch {
        throw new Error(
          'Failed to load HLS library or HLS is not supported in this browser.',
        );
      }
    }
  }, [
    episode?.id,
    loadNativePlaylistWithRetry,
    loadHLSPlaylistWithRetry,
    impressionSource,
  ]);

  const handlePlay = useCallback(async () => {
    trackClick({
      source: `${impressionSource}.play`,
      extra: {
        work_id: workId,
      },
    });
    if (!initializedStream) {
      incrementGenerateCount();
      await handleAudioStream();
      setInitializedStream(true);
    } else {
      setTimes((prevTimes) => [...prevTimes, new Date()]);
      const playPromise = audioRef.current?.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.error('Failed to resume playback:', error);
          // You could potentially show an error to the user here as well.
        });
      }
    }
  }, [
    workId,
    initializedStream,
    handleAudioStream,
    incrementGenerateCount,
    impressionSource,
  ]);

  const onSubmitFeedback = () => {
    fetchWithCsrf(
      routes.api_v0_arbitrary_event_index_path({ subdomain_param: 'api' }),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_type: 'PaperToPodcastFeedback',
          data: {
            feedback: feedbackInput,
            work_id: workId,
            type: 'swp',
          },
          // @ts-ignore
          hit_id: (window.Aedu && Aedu.hit_data && Aedu.hit_data.id) || null,
        }),
      },
    ).then(() => {
      setFeedbackSubmitted(true);
    });
  };

  const handleError = (error: Error | unknown) => {
    captureException(error);
    setError('Failed to generate audio summary.');
  };

  const handleRequestPodcast = useCallback(async () => {
    try {
      const response = await requestPodcast(Number(workId));
      if (response.success) {
        return { success: true, message: response.message };
      } else {
        return { success: false, error: response.error };
      }
    } catch (_error) {
      return { success: false, error: 'Failed to submit podcast request' };
    }
  }, [workId]);

  return {
    // State
    showPlayer,
    feedbackInput,
    loading,
    feedbackSubmitted,
    error,
    episode,
    audioRef,
    initializedStream,

    // Setters
    setFeedbackInput,
    setError,

    // Handlers
    handlePlay,
    handlePause,
    onSubmitFeedback,
    handleError,
    handleRequestPodcast,

    // Computed values
    isReady: !!episode,
    hasError: !!error,
    isPremiumUser: !!currentUser?.premium,
  };
};
